import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import {
  getApplicationById,
  getApplicationDocsById,
} from "../../actions/application.action";
import { getEnterpriseById } from "../../actions/enterprise.action";
import { getUserById } from "../../actions/user.action";
import { BANK_STATEMENT } from "../../constants";
import {
  RetailODApplicationDetails,
  TermLoanApplicationDetails,
  UserDetails,
} from "../../dto";
import { EnterpriseDetails } from "../../dto/enterprise.dto";
import useScreenName from "../../hooks/useScreenName";
import dispatch from "../../middleware";
import { ApplicantType, BaseApplication, RoutePaths } from "../../models";
import { ReduxState } from "../../reducers";
import { getUserSelfieDataUrl, isLoadingActive } from "../../utils";
import ApplicationCard from "./applicationCard";
import ApplicationDetails from "./applicationDetails";
import DocumentList from "./documentList";

export default function ApplicationDetailsScreen() {
  useScreenName("Applications");
  const { appId }: { appId: string } = useParams();
  const storeDispatch = useDispatch();
  const applicationDetails:
    | TermLoanApplicationDetails
    | RetailODApplicationDetails
    | null = useSelector(
    (state: ReduxState) => state.application.singleApplication,
  );
  const loadingQueue = useSelector(
    (state: ReduxState) => state.application.loadingQueue,
  );
  const [userDetails, setUserDetails] = useState<
    UserDetails | EnterpriseDetails | null
  >(null);
  const [selfieDataUrl, setSelfieDataUrl] = useState<string | null>(null);
  const applicationDocList = useSelector(
    (state: ReduxState) => state.application.docs,
  );
  const userDocList = useSelector(
    (state: ReduxState) => state.user.singleUserDocList,
  );
  const monthlyAnalysis = useSelector(
    (state: ReduxState) => state.application.monthlyAnalysis,
  );
  const isBankStatementsExists = useMemo(
    () => applicationDocList.includes(BANK_STATEMENT),
    [applicationDocList],
  );
  const [errorMessage, setErrorMessage] = useState("");

  function isTermLoanApplicationDetails(
    details: TermLoanApplicationDetails | RetailODApplicationDetails | null,
  ): details is TermLoanApplicationDetails {
    return details?.applicationType === "TERM_LOAN_APP";
  }

  // INFO: hack to get value
  // TODO: change interface types
  const tempVal: Record<string, any> = { ...applicationDetails };
  const amount = tempVal?.maxAmount ?? tempVal?.approvedAmount ?? "0";
  const isTermLoanApplication =
    isTermLoanApplicationDetails(applicationDetails);
  const creditDecisionId = isTermLoanApplication
    ? applicationDetails.creditDecisionId
    : undefined;
  const clientReferenceId = isTermLoanApplication
    ? applicationDetails.clientReferenceId
    : undefined;
  const creditDecisionLink = creditDecisionId
    ? `${RoutePaths.CREDIT_DECISION_AUDIT_LOG_DETAIL.replace(
        ":id",
        creditDecisionId,
      )}`
    : clientReferenceId
      ? `${RoutePaths.CREDIT_DECISION_AUDIT_LOGS}?clientApplicationId=${encodeURIComponent(clientReferenceId)}&evaluatedAt=all`
      : null;

  async function fetchData(appId: string) {
    try {
      const resp: BaseApplication = await dispatch(
        storeDispatch,
        getApplicationById(appId),
      );
      const isEnterprise =
        resp.applicantType === ApplicantType.ENTERPRISE || !!resp.enterpriseId;
      if (isEnterprise) {
        if (!resp.enterpriseId) return;
        const enterprise: EnterpriseDetails = await dispatch(
          storeDispatch,
          getEnterpriseById(resp.enterpriseId),
        );
        setUserDetails(enterprise);
      } else {
        if (!resp.userId) return;
        const user: UserDetails = await dispatch(
          storeDispatch,
          getUserById(resp.userId),
        );
        setUserDetails(user);
        setUserSelfie(user._id);
      }
    } catch (e) {
      setErrorMessage(e?.message);
    }
  }

  async function setUserSelfie(userId: string) {
    const dataUrl = await getUserSelfieDataUrl(storeDispatch, userId);
    if (!dataUrl) return;
    setSelfieDataUrl(dataUrl);
  }

  useEffect(() => {
    fetchData(appId);
    dispatch(storeDispatch, getApplicationDocsById(appId));
    return () => {
      // TODO: Clear application state on REDUX
    };
  }, [appId]);
  return (
    <div className="row" style={{ margin: "0" }}>
      <div className="col-12 mb-4">
        <ApplicationCard
          isLoading={isLoadingActive(loadingQueue)}
          appId={appId}
          applicationDetails={applicationDetails}
          userDetails={userDetails}
          errorMessage={errorMessage}
          selfieDataUrl={selfieDataUrl}
        />
      </div>
      <div className="col-12 mb-4">
        <ApplicationDetails
          isLoading={isLoadingActive(loadingQueue)}
          applicationDetails={applicationDetails}
          errorMessage={errorMessage}
        />
      </div>
      {userDetails && (
        <div className="col-12 mb-4">
          <DocumentList
            appId={appId}
            userId={userDetails._id}
            applicationDocList={applicationDocList}
            userDocList={userDocList}
            isLoading={isLoadingActive(loadingQueue)}
          />
        </div>
      )}
      {isTermLoanApplication && creditDecisionLink && (
        <div className="col-12 mb-4">
          <small className="font-size-small">
            <Link to={creditDecisionLink}>View credit decision</Link>
          </small>
        </div>
      )}
    </div>
  );
}
